const BASE = process.env.REACT_APP_API_URL || '/api';

// ─── Refresh token management ───
let _isRefreshing = false;
let _refreshQueue = [];

function _processQueue(newToken) {
  _refreshQueue.forEach(cb => cb(newToken));
  _refreshQueue = [];
}

async function _tryRefresh() {
  const rt = localStorage.getItem('admin_refresh_token');
  if (!rt) return null;
  try {
    const res = await fetch(`${BASE}/admin/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_refresh_token', data.refresh_token);
    localStorage.setItem('admin_user', JSON.stringify(data.user));
    return data.token;
  } catch { return null; }
}

async function request(method, path, body) {
  const token = localStorage.getItem('admin_token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = { method, headers };
  if (body) {
    headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(body);
  }

  let res = await fetch(`${BASE}${path}`, config);

  // On 401 — try refresh before logging out
  if (res.status === 401) {
    if (!_isRefreshing) {
      _isRefreshing = true;
      const newToken = await _tryRefresh();
      _isRefreshing = false;

      if (newToken) {
        _processQueue(newToken);
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryConfig = { method, headers };
        if (body) { retryConfig.body = JSON.stringify(body); }
        res = await fetch(`${BASE}${path}`, retryConfig);
      } else {
        _processQueue(null);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    } else {
      const newToken = await new Promise(resolve => _refreshQueue.push(resolve));
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryConfig = { method, headers };
        if (body) { retryConfig.body = JSON.stringify(body); }
        res = await fetch(`${BASE}${path}`, retryConfig);
      } else { throw new Error('Session expired'); }
    }
  }

  // Final 401 after retry
  if (res.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const api = {
  get:    (path)       => request('GET',    path),
  post:   (path, body) => request('POST',   path, body),
  put:    (path, body) => request('PUT',    path, body),
  delete: (path)       => request('DELETE', path),
  patch:  (path, body) => request('PATCH',  path, body),
};

export default api;

// ── Auth ─────────────────────────────────────────────────────────────────────
// Raw fetch — bypasses the 401 intercept so a bad password shows
// "Invalid credentials" instead of "Session expired"
export const login = async (email, password) => {
  const res  = await fetch(`${BASE}/admin/auth/login`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Invalid credentials');
  return data;
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const getStats        = ()         => api.get('/admin/stats');
export const getOrgs         = ()         => api.get('/admin/organizations');
export const getOrg          = (id)       => api.get(`/admin/organizations/${id}`);
export const updateOrg       = (id, data) => api.put(`/admin/organizations/${id}`, data);
export const suspendOrg      = (id)       => api.post(`/admin/organizations/${id}/suspend`);
export const activateOrg     = (id)       => api.post(`/admin/organizations/${id}/activate`);
export const impersonateUser = (userId)   => api.post(`/admin/impersonate/${userId}`);

// ── System Admins ─────────────────────────────────────────────────────────────
export const getSystemAdmins   = ()           => api.get('/admin/system-admins');
export const createSystemAdmin = (data)       => api.post('/admin/system-admins', data);
export const toggleSystemAdmin = (id, active) => api.patch(`/admin/system-admins/${id}`, { is_active: active });

// ── Users ─────────────────────────────────────────────────────────────────────
export const updateUserEmail = (userId, email) => api.patch(`/admin/users/${userId}/email`, { email });

// ── Email & Password Reset ────────────────────────────────────────────────────
export const sendResetEmail = (userId)  => api.post(`/admin/users/${userId}/send-reset`);
export const sendEmail      = (payload) => api.post('/admin/send-email', payload);
export const getAuditLogs   = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit)  qs.set('limit',  params.limit);
  if (params.offset) qs.set('offset', params.offset);
  if (params.action) qs.set('action', params.action);
  return api.get(`/admin/audit-logs?${qs.toString()}`);
};

// ── Medium Priority ───────────────────────────────────────────────────────────
export const getBillingStats = ()           => api.get('/admin/billing-stats');
export const searchUsers     = (q)          => api.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
export const bulkOrgAction   = (payload)    => api.post('/admin/organizations/bulk', payload);

// ── Nice to Have ─────────────────────────────────────────────────────────────
export const getOrgStats   = (id)     => api.get(`/admin/organizations/${id}/stats`);
export const getOrgNotes   = (id)     => api.get(`/admin/organizations/${id}/notes`);
export const addOrgNote    = (id, body) => api.post(`/admin/organizations/${id}/notes`, { body });
export const deleteOrgNote = (orgId, noteId) => api.delete(`/admin/organizations/${orgId}/notes/${noteId}`);
// Token-authenticated CSV downloads
async function downloadCSV(path, filename) {
  const token = localStorage.getItem('admin_token');
  const BASE = process.env.REACT_APP_API_URL || '';
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
export const exportOrgsCSV  = () => downloadCSV('/admin/export/orgs',  `zeuscms-orgs-${new Date().toISOString().split('T')[0]}.csv`);
export const exportUsersCSV = () => downloadCSV('/admin/export/users', `zeuscms-users-${new Date().toISOString().split('T')[0]}.csv`);

// ── Plan Templates ─────────────────────────────────────────────────────────────
export const getPlans       = ()              => api.get('/admin/plans');
export const updatePlan     = (id, data)      => api.put(`/admin/plans/${id}`, data);
export const applyPlan      = (org_id, plan_slug) => api.post('/admin/plans/apply', { org_id, plan_slug });

// ── Support Tickets (Admin) ───────────────────────────────────────────────────
export const getTickets      = (params = {}) => { const q = new URLSearchParams(params).toString(); return api.get(`/admin/support${q ? `?${q}` : ''}`); };
export const getTicket       = (id)          => api.get(`/admin/support/${id}`);
export const updateTicket    = (id, data)    => api.patch(`/admin/support/${id}`, data);
export const replyTicket     = (id, body)    => api.post(`/admin/support/${id}/reply`, { body });

// ── Push Notifications ────────────────────────────────────────────────────────
export const getVapidPublicKey      = ()    => api.get('/admin/push/vapid-public-key');
export const getPushStatus          = ()    => api.get('/admin/push/status');
export const subscribeToAdminPush   = (sub) => api.post('/admin/push/subscribe',   { endpoint: sub.endpoint, keys: sub.keys });
export const unsubscribeFromAdminPush=(ep)  => api.post('/admin/push/unsubscribe', { endpoint: ep });
export const getSystemErrors = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit)  qs.set('limit',  params.limit);
  if (params.offset) qs.set('offset', params.offset);
  if (params.type)   qs.set('type',   params.type);
  return api.get(`/admin/system-errors?${qs.toString()}`);
};

// ── Session Management ───────────────────────────────────────────────────────
export const getAdminSessions       = ()   => api.get('/admin/auth/sessions');
export const revokeAdminSessionById = (id) => api.delete(`/admin/auth/sessions/${id}`);
export const revokeAllAdminSessions = ()   => api.delete('/admin/auth/sessions/all');
export const logoutAdminSession     = ()   => {
  const rt = localStorage.getItem('admin_refresh_token');
  return api.post('/admin/auth/logout', { refresh_token: rt });
};

// ── Security Health ──────────────────────────────────────────────────────────
export const getSecurityHealth = () => api.get('/admin/security-health');

// ── Data Audit Trail ────────────────────────────────────────────────────────
export const getDataAudit = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, v); });
  return api.get(`/admin/data-audit?${qs.toString()}`);
};
export const getDataAuditTables = () => api.get('/admin/data-audit/tables');

// ── API Usage ────────────────────────────────────────────────────────────────
export const getApiUsageAll     = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.days)   qs.set('days',   params.days);
  if (params.org_id) qs.set('org_id', params.org_id);
  return api.get(`/admin/api-usage/all?${qs.toString()}`);
};
export const getApiUsageService = (service, params = {}) => {
  const qs = new URLSearchParams();
  qs.set('service', service);
  if (params.days)   qs.set('days',   params.days);
  if (params.org_id) qs.set('org_id', params.org_id);
  return api.get(`/admin/api-usage?${qs.toString()}`);
};


// ── Latency Monitor ─────────────────────────────────────────────────────────
export const getLatencyTest = () => api.get('/admin/latency');
export const getHealthCheck = async () => {
  const CMS_URL = process.env.REACT_APP_CMS_API_URL || process.env.REACT_APP_API_URL || '/api';
  const start = Date.now();
  try {
    const res = await fetch(CMS_URL.replace(/\/api$/, '') + '/api/health');
    const roundTrip = Date.now() - start;
    const data = await res.json();
    return { ...data, roundtrip_ms: roundTrip };
  } catch (e) {
    return { status: 'error', roundtrip_ms: Date.now() - start, error: e.message };
  }
};
